import React, {useCallback} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import {SMCCheckbox} from '../../../../../components/Simple/SMCCheckbox';
import {useTranslation} from 'react-i18next';
import PlanEditorInnerContainer from '../../../../../components/Editor/PlanEditorInnerContainer';
import Separator from '../../../../../components/Simple/Sepperator';
import SMCLabel from '../../../../../components/Simple/SMCLabel';
import EmptyRow from '../../../../../components/Simple/EmptyRow';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import {SMCRadioGroup, SMCRadio} from '../../../../../components/Simple/SMCRadio';
import SMCNote from '../../../../../components/Simple/SMCNote';
import sslInspectionIcon from '../../../../../assets/images/ssl_inspection_64.png'
import RowLayout from '../../../../../components/Layouts/RowLayout';
import Indentation from '../../../../../components/Simple/Indentation';
import SMCLink from '../../../../../components/Simple/SMCLink';
import AppiEditorModal from '../../../../PopupPages/AppiEditorModal';
import SSLCustomSitesEditorModal from '../../../../PopupPages/SSLCustomSitesEditorModal';
import {useBoolean} from '../../../../../utils/hooks';
import {SMCToggleSwitch} from "../../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.sslInspection;
const pageId = pageSchema.pageId;

function BaseSSLInspection({
                               pageData,
                               setObjectData,
                               pageDisabled,
                               baseObjectName,
                               objectType,
                               setAppiDataValue,
                           }) {
    const {t} = useTranslation();

    const manageInSmp = pageData[pageSchema.manageInSmp];
    const mode = pageData[pageSchema.mode];
    const policy = pageData[pageSchema.policy];
    const inspectHttps = pageData[pageSchema.inspectHttps];
    const inspectImaps = pageData[pageSchema.inspectImaps];
    const trustedNetworks = pageData[pageSchema.trustedNetworks];
    const untrustedNetworks = pageData[pageSchema.untrustedNetworks];
    const healthCategory = pageData[pageSchema.healthCategory];
    const governmentCategory = pageData[pageSchema.governmentCategory];
    const financeServiceCategory = pageData[pageSchema.financeServiceCategory];
    const mediaCategory = pageData[pageSchema.mediaCategory];
    const updateCategory = pageData[pageSchema.updateCategory];
    const bypassOtherCategory = pageData[pageSchema.bypassOtherCategory];
    const sslBlockList = pageData[pageSchema.sslBlockList];
    const bypassCustomSites = pageData[pageSchema.bypassCustomSites];
    const customSites = pageData[pageSchema.customSites];
    const trackInspectLogs = pageData[pageSchema.trackInspectLogs];
    const trackBypassLogs = pageData[pageSchema.trackBypassLogs];
    const trackWirelessBypassLogs = pageData[pageSchema.trackWirelessBypassLogs];

    const policyValues = pageSchema.policySchema;

    const [isEditSSLBlockListOpen, openEditSSLBlockList, closeEditSSLBlockList] = useBoolean(false);
    const [isEditCustomSitesOpen, openEditSCustomSites, closeEditSCustomSites] = useBoolean(false);

    const setPageValue = useCallback((param, newValue) => {
        setObjectData([pageId, param], newValue);
    }, [setObjectData]);

    return (
        <EditorLayout>
            <SMCToggleSwitch
                disabled={pageDisabled}
                checked={manageInSmp}
                onChange={checked => setPageValue(pageSchema.manageInSmp, checked)}
                label={t('SMP.PLAN.MANAGE_IN_SMP')}
            />
            <Separator isMainSeparator/>
            <SMCNote>
                {t('SMP.PLAN.SEC_SOFT_BLADES.LINE3')}&nbsp;
                <a href="https://supportcenter.checkpoint.com/supportcenter/portal?eventSubmit_doGoviewsolutiondetails=&solutionid=sk121214"
                   rel="noopener noreferrer" target="_blank">sk121214</a>&nbsp;
                {t('SMP.PLAN.SEC_SOFT_BLADES.LINE4')}
            </SMCNote>
            <SMCNote>
                {t('SMP.PLAN.SEC_SOFT_BLADES.ZONE_NOTE')}
            </SMCNote>
            <PlanEditorInnerContainer title={t('SMP.PLANS.INNER_MENU.SSL_INSPECTION')} managedInSmp={manageInSmp}>
                <RowLayout>
                    <img alt="" src={sslInspectionIcon}/>
                    <SMCRadioGroup value={mode ? 'ON' : 'OFF'} onChange={e => setPageValue(pageSchema.mode, e.target.value === 'ON')} disabled={pageDisabled || !manageInSmp}>
                        <SMCRadio value="ON" label={t('SMP.ON')}/>
                        <SMCRadio value="OFF" label={t('SMP.OFF')}/>
                    </SMCRadioGroup>
                </RowLayout>
                <EmptyRow/>
                {/* Policy */}
                <SMCLabel bold>{t('SMP.PLAN.SEC_SOFT_BLADES.POLICY')}:</SMCLabel>
                <SMCRadioGroup
                    disabled={pageDisabled || !manageInSmp}
                    value={policy}
                    onChange={e => setPageValue(pageSchema.policy, e.target.value)}
                >
                    <Indentation indentation={40}>
                        <SMCRadio
                            value={policyValues.sslTrafficInspection}
                            label={t('SMP.PLAN.INSPECTION_SSL.SSL_TRAFFIC_INSPECTION')}
                            tooltip={t('SMP.PLAN.INSPECTION_SSL.SSL_TRAFFIC_INSPECTION.TOOLTIP')}
                        />
                        <SMCRadio
                            value={policyValues.httpsCategorization}
                            label={t('SMP.PLAN.INSPECTION_SSL.HTTPS_CATEGORIZATION')}
                            tooltip={t('SMP.PLAN.INSPECTION_SSL.HTTPS_CATEGORIZATION.TOOLTIP')}
                        />
                    </Indentation>
                </SMCRadioGroup>
                <EmptyRow/>
                {/* Protocols to inspect */}
                <SMCLabel bold>{t('SMP.PLAN.INSPECTION_SSL.PROTOCOL_TO_INSPECT')}:</SMCLabel>
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={inspectHttps}
                    onChange={e => setPageValue(pageSchema.inspectHttps, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.HTTPS')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.HTTPS.TOOLTIP')}
                />
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={inspectImaps}
                    onChange={e => setPageValue(pageSchema.inspectImaps, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.IMAPS')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.IMAPS.TOOLTIP')}
                />
                <EmptyRow/>
                {/* Wireless networks to bypass */}
                <SMCLabel bold>{t('SMP.PLAN.INSPECTION_SSL.WIRELESS_NET_BYPASS')}:</SMCLabel>
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={trustedNetworks}
                    onChange={e => setPageValue(pageSchema.trustedNetworks, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.TRUSTED_NETWORKS')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.TRUSTED_NETWORKS.TOOLTIP')}
                />
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={untrustedNetworks}
                    onChange={e => setPageValue(pageSchema.untrustedNetworks, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.UNTRUSTED_NETWORKS')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.UNTRUSTED_NETWORKS.TOOLTIP')}
                />
                <EmptyRow/>
                {/* Categories to bypass */}
                <SMCLabel bold>{t('SMP.PLAN.INSPECTION_SSL.CATEGORIES_TO_BYPASS')}:</SMCLabel>
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={healthCategory}
                    onChange={e => setPageValue(pageSchema.healthCategory, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.HEALTH')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.HEALTH.TOOLTIP')}
                />
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={governmentCategory}
                    onChange={e => setPageValue(pageSchema.governmentCategory, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.GOVERNMENT_MILITARY')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.GOVERNMENT_MILITARY.TOOLTIP')}
                />
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={financeServiceCategory}
                    onChange={e => setPageValue(pageSchema.financeServiceCategory, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.FINANCIAL_SERVICES')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.FINANCIAL_SERVICES.TOOLTIP')}
                />
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={mediaCategory}
                    onChange={e => setPageValue(pageSchema.mediaCategory, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.MEDIA_STREAMS')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.MEDIA_STREAMS.TOOLTIP')}
                />
                <RowLayout>
                    <SMCCheckbox
                        disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                        checked={updateCategory}
                        onChange={e => setPageValue(pageSchema.updateCategory, e.target.checked)}
                        label={t('SMP.PLAN.INSPECTION_SSL.WELL_KNOWN_SERVICES')}
                        inline
                    />
                    |
                    <SMCLink
                        href="https://supportcenter.checkpoint.com/supportcenter/portal?eventSubmit_doGoviewsolutiondetails=&solutionid=sk98655&partition=Advanced&product=Security&lang=en&app=inclient&prod=1&date=1&oem=1000&dest=whitelist&lang=en&app=inclient&prod=1&date=1&oem=1000&dest=whitelist"
                        openInNewTab
                        disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    >
                        {t('SMP.PLAN.INSPECTION_SSL.WELL_KNOWN_SERVICES.MORE_INFO')}
                    </SMCLink>
                </RowLayout>
                <RowLayout>
                    <SMCCheckbox
                        disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                        checked={bypassOtherCategory}
                        onChange={e => setPageValue(pageSchema.bypassOtherCategory, e.target.checked)}
                        label={t('SMP.PLAN.INSPECTION_SSL.BYPASS')}
                        inline
                    />&nbsp;
                    <SMCLink
                        disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                        onClick={openEditSSLBlockList}
                    >
                        {t('SMP.PLAN.INSPECTION_SSL.OTHER_CATEGORIES')}
                    </SMCLink>
                </RowLayout>
                <RowLayout>
                    <SMCCheckbox
                        disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                        checked={bypassCustomSites}
                        onChange={e => setPageValue(pageSchema.bypassCustomSites, e.target.checked)}
                        label={t('SMP.PLAN.INSPECTION_SSL.BYPASS')}
                        inline
                    />&nbsp;
                    <SMCLink
                        disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                        onClick={openEditSCustomSites}
                    >
                        {t('SMP.PLAN.INSPECTION_SSL.CUSTOM_SITES')}
                    </SMCLink>
                    <SMCLabel
                        tooltip={t('SMP.PLAN.INSPECTION_SSL.BYPASS_SITES_NOTE.LINE1')}
                    />
                </RowLayout>

                <EmptyRow/>

                <SMCLabel bold>{t('SMP.PLAN.INSPECTION_SSL.TRACKING')}:</SMCLabel>
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={trackInspectLogs}
                    onChange={e => setPageValue(pageSchema.trackInspectLogs, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.ENABLE_INSPECT_LOGS')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.ENABLE_INSPECT_LOGS.TOOLTIP')}
                />
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={trackBypassLogs}
                    onChange={e => setPageValue(pageSchema.trackBypassLogs, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.ENABLE_BYPASS_lOGS')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.ENABLE_BYPASS_lOGS.TOOLTIP')}
                />
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp || policy === policyValues.httpsCategorization}
                    checked={trackWirelessBypassLogs}
                    onChange={e => setPageValue(pageSchema.trackWirelessBypassLogs, e.target.checked)}
                    label={t('SMP.PLAN.INSPECTION_SSL.ENABLE_WIRELESS_BYPASS_LOGS')}
                    tooltip={t('SMP.PLAN.INSPECTION_SSL.ENABLE_WIRELESS_BYPASS_LOGS.TOOLTIP')}
                />

                <EmptyRow/>

                <SMCLabel><b>{t('SMP.DESCRIPTION')}: </b>{t('SMP.PLAN.INSPECTION_SSL.DESCRIPTION_TEXT')}</SMCLabel>

            </PlanEditorInnerContainer>
            {/* Floating windowns */}
            {isEditSSLBlockListOpen && <AppiEditorModal
                isOpen={isEditSSLBlockListOpen}
                onRequestClose={closeEditSSLBlockList}
                deafultSelected={sslBlockList ? sslBlockList.split(',') : []}
                onSuccess={newSSLBlockList => {
                    setPageValue(pageSchema.sslBlockList, newSSLBlockList.join(','));
                    closeEditSSLBlockList();
                }}
                appType="SSL_INSPECTION"
                setAppiDataValue={setAppiDataValue}
            />}
            <SSLCustomSitesEditorModal
                customSites={customSites}
                isOpen={isEditCustomSitesOpen}
                onRequestClose={closeEditSCustomSites}
                onSuccess={newCustomSites => {
                    setPageValue(pageSchema.customSites, newCustomSites);
                    closeEditSCustomSites();
                }}
                baseObjectName={baseObjectName}
                objectType={objectType}
            />

        </EditorLayout>
    );
}

export default BaseSSLInspection;